import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent } from './ui/card';
import { Star, Quote, ChevronLeft, ChevronRight } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Testimonials = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeIndex, setActiveIndex] = useState(0);
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(true); // Start visible

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => setIsVisible(entry.isIntersecting),
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const response = await axios.get(`${API}/testimonials/`);
        setTestimonials(response.data);
      } catch (error) {
        console.error('Error fetching testimonials:', error);
        setTestimonials([
          { id: "1", name: "עמיר ויוסף", text: "שירות מקצועי ויוצא דופן! רכשנו אאודי חדש ואנחנו מרוצים מכל שלב בתהליך.", rating: 5, carPurchased: "אאודי Q3", image: "https://customer-assets.emergentagent.com/job_elite-motors-13/artifacts/hpin640o_%D7%A6%D7%99%D7%9C%D7%95%D7%9D%20%D7%9E%D7%A1%D7%9A%202025-09-16%20%D7%91-18.09.39.png" },
          { id: "2", name: "דני ויועל", text: "חוויית רכישה נפלאה! הרכב בדיוק מה שחיפשנו והשירות היה ברמה הגבוהה ביותר.", rating: 5, carPurchased: "שקודה קארוק", image: "https://customer-assets.emergentagent.com/job_elite-motors-13/artifacts/lmxzvivf_%D7%A6%D7%99%D7%9C%D7%95%D7%9D%20%D7%9E%D7%A1%D7%9A%202025-09-16%20%D7%91-18.10.34.png" },
          { id: "3", name: "שירה ואלי", text: "אני כל כך מרוצה מהרכב החדש שלי! השירות היה מעולה והצוות עזר לי למצוא בדיוק מה שחיפשתי.", rating: 5, carPurchased: "SUV יוקרתי", image: "https://customer-assets.emergentagent.com/job_elite-motors-13/artifacts/l5ku0xod_%D7%A6%D7%99%D7%9C%D7%95%D7%9D%20%D7%9E%D7%A1%D7%9A%202025-09-16%20%D7%91-18.11.21.png" },
          { id: "4", name: "רועי ועמית", text: "BMW חדש מהחלומות! התהליך היה מהיר, מקצועי ונעים. J CLASS MOTORS הוכיחו שהם הטובים ביותר בתחום.", rating: 5, carPurchased: "BMW יוקרתי", image: "https://customer-assets.emergentagent.com/job_elite-motors-13/artifacts/k1wwxyc4_%D7%A6%D7%99%D7%9C%D7%95%D7%9D%20%D7%9E%D7%A1%D7%9A%202025-09-16%20%D7%91-18.11.43.png" }
        ]);
      } finally {
        setLoading(false);
      }
    };
    fetchTestimonials();
  }, []);

  const renderStars = (rating) => (
    <div className="flex gap-1">
      {Array.from({ length: 5 }, (_, i) => (
        <Star key={i} className={`w-4 h-4 ${i < rating ? 'text-luxury-gold fill-luxury-gold' : 'text-luxury-border'}`} />
      ))}
    </div>
  );

  const nextSlide = () => setActiveIndex((prev) => (prev + 1) % testimonials.length);
  const prevSlide = () => setActiveIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);

  useEffect(() => {
    if (testimonials.length > 0) {
      const timer = setInterval(nextSlide, 5000);
      return () => clearInterval(timer);
    }
  }, [testimonials.length]);

  if (loading) {
    return (
      <section className="py-24 bg-luxury-surface">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <motion.p animate={{ opacity: [0.5, 1, 0.5] }} transition={{ duration: 1.5, repeat: Infinity }} className="text-luxury-gold text-xl">
            טוען המלצות...
          </motion.p>
        </div>
      </section>
    );
  }

  return (
    <section ref={sectionRef} className="py-24 bg-luxury-surface relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 pointer-events-none opacity-5">
        <div className="absolute top-20 right-20 w-96 h-96 border border-luxury-gold rounded-full" />
        <div className="absolute bottom-20 left-20 w-64 h-64 border border-luxury-gold rounded-full" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 relative z-10">
        {/* Header */}
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="h-px w-12 bg-luxury-gold" />
            <span className="text-luxury-gold text-sm font-medium tracking-[0.3em] uppercase">מה הלקוחות אומרים</span>
            <div className="h-px w-12 bg-luxury-gold" />
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-luxury-text mb-6">
            הלקוחות <span className="gold-shimmer">ממליצים</span>
          </h2>
          <p className="text-lg text-luxury-muted max-w-2xl mx-auto">
            הצטרפו לאלפי לקוחות מרוצים שכבר בחרו בנו
          </p>
        </motion.div>

        {/* Featured Testimonial */}
        <motion.div 
          className="relative mb-12"
          initial={{ opacity: 0, y: 40 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <div className="glass border border-luxury-border p-8 md:p-12 relative overflow-hidden">
            {/* Quote Icon */}
            <Quote className="absolute top-8 right-8 w-16 h-16 text-luxury-gold/10" />
            
            <div className="grid md:grid-cols-2 gap-8 items-center">
              {/* Image */}
              <motion.div 
                key={activeIndex}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5 }}
                className="relative"
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img 
                    src={testimonials[activeIndex]?.image} 
                    alt={testimonials[activeIndex]?.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                {/* Gold corner accent */}
                <div className="absolute -bottom-3 -right-3 w-24 h-24 border-r-2 border-b-2 border-luxury-gold" />
              </motion.div>

              {/* Content */}
              <motion.div 
                key={`content-${activeIndex}`}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5 }}
                className="space-y-6"
              >
                {renderStars(testimonials[activeIndex]?.rating || 5)}
                
                <blockquote className="text-xl md:text-2xl text-luxury-text leading-relaxed">
                  "{testimonials[activeIndex]?.text}"
                </blockquote>

                <div className="pt-4 border-t border-luxury-border">
                  <p className="text-luxury-gold font-bold text-lg">{testimonials[activeIndex]?.name}</p>
                  <p className="text-luxury-muted">רכש: {testimonials[activeIndex]?.carPurchased || testimonials[activeIndex]?.car || 'רכב יוקרתי'}</p>
                </div>
              </motion.div>
            </div>

            {/* Navigation */}
            <div className="flex items-center justify-between mt-8 pt-6 border-t border-luxury-border">
              <div className="flex gap-2">
                {testimonials.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveIndex(i)}
                    className={`h-1 transition-all duration-300 ${i === activeIndex ? 'w-8 bg-luxury-gold' : 'w-4 bg-luxury-border hover:bg-luxury-muted'}`}
                  />
                ))}
              </div>
              
              <div className="flex gap-2">
                <motion.button
                  onClick={prevSlide}
                  className="w-12 h-12 flex items-center justify-center border border-luxury-border hover:border-luxury-gold hover:bg-luxury-gold text-luxury-text hover:text-luxury-black transition-all duration-300"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <ChevronRight className="w-5 h-5" />
                </motion.button>
                <motion.button
                  onClick={nextSlide}
                  className="w-12 h-12 flex items-center justify-center border border-luxury-border hover:border-luxury-gold hover:bg-luxury-gold text-luxury-text hover:text-luxury-black transition-all duration-300"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <ChevronLeft className="w-5 h-5" />
                </motion.button>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Small Cards Grid */}
        <motion.div 
          className="grid grid-cols-2 md:grid-cols-4 gap-4"
          initial={{ opacity: 0, y: 30 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          {testimonials.map((t, i) => (
            <motion.button
              key={t.id}
              onClick={() => setActiveIndex(i)}
              className={`p-4 text-right transition-all duration-300 ${i === activeIndex ? 'bg-luxury-gold/10 border-luxury-gold' : 'bg-luxury-surface-light border-luxury-border hover:border-luxury-gold/50'} border`}
              whileHover={{ y: -4 }}
            >
              <div className="flex gap-0.5 mb-2">
                {Array.from({ length: 5 }, (_, j) => (
                  <Star key={j} className="w-3 h-3 text-luxury-gold fill-luxury-gold" />
                ))}
              </div>
              <p className="text-luxury-text font-medium text-sm truncate">{t.name}</p>
              <p className="text-luxury-muted text-xs truncate">{t.carPurchased || t.car || 'רכב יוקרתי'}</p>
            </motion.button>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Testimonials;
